import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Check } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { adoptionsApi, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

export default function AdoptionSubmit() {
    const navigate = useNavigate();
    const [cats, setCats] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        title: "",
        category: "partner_gesucht",
        description: "",
        plz: "",
        location: "",
        health_status: "",
        flight_capable: false,
        sex: "unbekannt",
        owner_name: "",
        owner_email: "",
    });
    const [images, setImages] = useState([]);
    const [consent, setConsent] = useState(false);

    useEffect(() => {
        adoptionsApi.categories().then((d) => setCats(d.categories || [])).catch(() => {});
    }, []);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        if (!consent) return toast.error("Bitte Einwilligung bestätigen");
        if (!form.title || !form.owner_email) return toast.error("Bitte Pflichtfelder ausfüllen");
        setSubmitting(true);
        try {
            const compressed = await Promise.all(
                images.map((f) => (f.type?.startsWith("image/") ? compressImage(f) : Promise.resolve(f)))
            );
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
            compressed.forEach((f) => fd.append("images", f));
            await adoptionsApi.submit(fd);
            setDone(true);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <SiteLayout>
                <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="submit-success">
                    <Check size={32} className="text-emerald-300 mx-auto mb-6" />
                    <h1 className="font-display text-4xl mb-4">Inserat veröffentlicht</h1>
                    <p className="text-[#94a3b8] mb-6 leading-relaxed">
                        Dein Inserat ist sofort im Bereich Vermittlung sichtbar. Wir haben dir
                        außerdem einen <strong className="text-[#f8fafc]">persönlichen
                        Bearbeitungs-Link</strong> an{" "}
                        <span className="font-mono text-[#f0c674]">{form.owner_email}</span>{" "}
                        geschickt. Damit kannst du dein Inserat jederzeit ändern oder löschen.
                    </p>
                    <p className="text-[#64748b] text-sm mb-10">
                        Tipp: Speichere die Mail. Falls du den Link verlierst, kannst du auf der
                        Vermittlungs-Seite jederzeit einen neuen anfordern.
                    </p>
                    <div className="flex gap-3 justify-center flex-wrap">
                        <Link to="/vermittlung" className="btn-primary">
                            Zur Vermittlung
                        </Link>
                        <button className="btn-ghost" onClick={() => { setDone(false); setForm({ ...form, title: "" }); setImages([]); }}>
                            Weiteres Inserat aufgeben
                        </button>
                    </div>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link to="/vermittlung" className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10">
                    <ArrowLeft size={14} /> Zurück zur Vermittlung
                </Link>
                <div className="label mb-3">Taubenvermittlung</div>
                <h1 className="font-display text-4xl sm:text-5xl mb-4">Inserat aufgeben</h1>
                <p className="text-[#94a3b8] mb-10 leading-relaxed">
                    Du suchst für eine Taube ein Zuhause, eine Pflegestelle oder einen Partner?
                    Lege hier dein Inserat an. Du erhältst einen persönlichen Bearbeitungs-Link
                    per E-Mail — kein Login, kein Passwort.
                </p>

                <form onSubmit={submit} className="space-y-5" data-testid="submit-form">
                    <Field label="Titel des Inserats" required>
                        <input value={form.title} onChange={(e) => set("title", e.target.value)} className="input-field" required data-testid="submit-title" placeholder="z. B. Cleo sucht eine Endstelle" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        <Field label="Kategorie" required>
                            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="input-field" data-testid="submit-category">
                                {cats.map((c) => (
                                    <option key={c.key} value={c.key} className="bg-[#0c1222]">{c.label}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Geschlecht">
                            <select value={form.sex} onChange={(e) => set("sex", e.target.value)} className="input-field">
                                <option value="unbekannt" className="bg-[#0c1222]">Unbekannt</option>
                                <option value="m" className="bg-[#0c1222]">Männlich</option>
                                <option value="w" className="bg-[#0c1222]">Weiblich</option>
                            </select>
                        </Field>
                        <Field label="Flugfähig?">
                            <label className="flex items-center gap-2 input-field cursor-pointer">
                                <input type="checkbox" checked={form.flight_capable} onChange={(e) => set("flight_capable", e.target.checked)} className="accent-[#f0c674]" data-testid="submit-flight" />
                                Ja, flugfähig
                            </label>
                        </Field>
                    </div>
                    <Field label="Beschreibung">
                        <textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="input-field" rows={5} data-testid="submit-description" placeholder="Erzähle etwas über die Taube — Geschichte, Charakter, besondere Bedürfnisse…" />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="PLZ">
                            <input value={form.plz} onChange={(e) => set("plz", e.target.value)} className="input-field" data-testid="submit-plz" />
                        </Field>
                        <Field label="Ort / Stadt">
                            <input value={form.location} onChange={(e) => set("location", e.target.value)} className="input-field" data-testid="submit-location" />
                        </Field>
                    </div>
                    <Field label="Gesundheitszustand">
                        <input value={form.health_status} onChange={(e) => set("health_status", e.target.value)} className="input-field" data-testid="submit-health" placeholder="z. B. Trichomonaden behandelt, Augenlid-Verletzung verheilt" />
                    </Field>
                    <Field label="Bilder (bis 8)">
                        <FileDrop files={images} setFiles={setImages} accept="image/*" testid="submit-images" max={8} />
                    </Field>

                    <div className="border-t border-white/5 pt-6">
                        <div className="label mb-3">Deine Kontaktdaten</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Dein Name">
                                <input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="input-field" data-testid="submit-owner-name" />
                            </Field>
                            <Field label="E-Mail (für Bearbeitungs-Link)" required>
                                <input type="email" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} className="input-field" required data-testid="submit-owner-email" />
                            </Field>
                        </div>
                        <p className="text-xs text-[#64748b] mt-3">
                            Die E-Mail wird Interessent:innen <strong className="text-[#94a3b8]">nicht</strong> direkt angezeigt. Anfragen erhältst du über das Kontaktformular.
                        </p>
                    </div>

                    <label className="flex items-start gap-3 text-sm text-[#94a3b8] leading-relaxed">
                        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#f0c674]" data-testid="submit-consent" />
                        <span>
                            Ich willige ein, dass die Inseratsdaten und mein Bild öffentlich
                            angezeigt werden. Meine E-Mail wird nur für den Bearbeitungs-Link
                            und Anfragen genutzt. Siehe{" "}
                            <Link to="/datenschutz" className="text-[#f0c674] underline">Datenschutzerklärung</Link>.
                        </span>
                    </label>

                    <button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-button">
                        {submitting ? "Wird gesendet…" : "Inserat veröffentlichen"}
                    </button>
                </form>
            </section>
        </SiteLayout>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-sm text-[#94a3b8] mb-2">
                {label} {required && <span className="text-[#f0c674]">*</span>}
            </span>
            {children}
        </label>
    );
}

function FileDrop({ files, setFiles, accept, testid, max = 5 }) {
    function add(e) {
        const list = Array.from(e.target.files || []);
        setFiles((cur) => [...cur, ...list].slice(0, max));
    }
    function remove(idx) {
        setFiles((cur) => cur.filter((_, i) => i !== idx));
    }
    return (
        <div>
            <label htmlFor={testid} className="cursor-pointer flex items-center gap-3 input-field hover:border-[#f0c674]/40">
                <Upload size={16} className="text-[#f0c674]" />
                <span className="text-sm text-[#94a3b8]">{files.length > 0 ? `${files.length} Datei(en) ausgewählt` : "Bilder auswählen"}</span>
            </label>
            <input id={testid} type="file" multiple accept={accept} onChange={add} className="hidden" data-testid={testid} />
            {files.length > 0 && (
                <ul className="mt-3 space-y-1">
                    {files.map((f, i) => (
                        <li key={i} className="text-xs text-[#94a3b8] flex items-center justify-between glass px-3 py-2">
                            <span className="truncate">{f.name}</span>
                            <button type="button" className="text-[#64748b] hover:text-rose-400" onClick={() => remove(i)}>entfernen</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
